import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { useLocalData } from "../hooks/useLocalData";
import { useAuth } from "../hooks/useAuth";
import { createSession, updateSession } from "../services/dataService";
import { extractKeyFacts } from "../services/aiService";
import { pushSafetyAlert } from "../services/safetyAlertService";
import { updateAgentLiveStatus } from "../services/firestoreSync";
import { Pause, Play, Flag, Square, Wifi, WifiOff, Save, Clock, FileText, Shield, MapPin } from "lucide-react";
import {
  saveRecoveryData,
  getRecoveryData,
  clearRecoveryData,
} from "../services/sessionRecoveryService";

// Auto-save interval in ms
const AUTO_SAVE_INTERVAL = 10000; // IndexedDB checkpoint every 10 seconds
const EXTRACTION_INTERVAL = 120000; // Extract facts every 2 minutes

export default function ActiveSessionPage() {
  const { sessionId: paramSessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline } = useOnlineStatus();
  const { addNote, updateNote, recordDraftSession, completeAppointment } = useLocalData();
  const { user, profile, setRecording } = useAuth();
  const [sessionId, setSessionId] = useState(paramSessionId || null);
  const [showEndMenu, setShowEndMenu] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [waveHeights, setWaveHeights] = useState([]);
  const [extractedFacts, setExtractedFacts] = useState([]);
  const [extractionRunning, setExtractionRunning] = useState(false);
  const [showFacts, setShowFacts] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const autoSaveRef = useRef(null);
  const extractionRef = useRef(null);
  const lastExtractedIndex = useRef(0);

  // Pull individual info from route state (passed from dashboard)
  const indInfo = {
    individualName: location.state?.individualName || "Individual",
    dob: location.state?.dob || "",
    locationType: location.state?.locationType || "Out-of-Clinic",
    location: location.state?.location || "Community",
    serviceType: location.state?.serviceType || "ICM Weekly Visit",
    individualId: location.state?.individualId || "unknown",
  };

  // Continued session — carries forward the previous transcript
  const existingTranscript = location.state?.existingTranscript || "";
  const existingNote = location.state?.existingNote || null;
  const continuedDraftId = location.state?.draftNoteId || null;
  const [isContinuation] = useState(!!existingTranscript);

  const {
    isRecording,
    isPaused,
    isFlagging,
    flagPriority,
    flagAlert,
    formattedTime,
    transcript,
    fullTranscript,
    flaggedNotes,
    currentSection,
    lastTrigger,
    error,
    initialize,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getSessionData,
    manualFlag,
    cleanup,
  } = useAudioRecorder(sessionId);

  // ===== REAL-TIME SAFETY ALERTS =====
  // ONLY Tier 3 (realtimeAlert) pushes to supervisor dashboard.
  // Tier 1 (localFlag) and Tier 2 (clinicalIndicator) stay local.
  const lastPushedAlert = useRef(null);
  useEffect(() => {
    if (!flagAlert) return;
    
    // Only push Tier 3 alerts to supervisor
    if (flagAlert.tier === 3 && flagAlert.type === "realtimeAlert" && flagAlert.phrase !== lastPushedAlert.current) {
      lastPushedAlert.current = flagAlert.phrase;
      
      // Get surrounding transcript for full context (500 chars before and after)
      const currentPos = fullTranscript.length;
      const contextStart = Math.max(0, currentPos - 500);
      const transcriptContext = fullTranscript.substring(contextStart);

      pushSafetyAlert({
        triggerPhrase: flagAlert.phrase,
        triggerType: flagAlert.type,
        category: flagAlert.category || "Safety Concern",
        severity: flagAlert.severity || "high",
        individualName: indInfo.individualName,
        individualId: indInfo.individualId,
        sessionId: sessionId,
        transcriptSnippet: transcriptContext,
        transcriptFull: fullTranscript,
        triggerTimestamp: flagAlert.timestamp,
        agentLocation: indInfo.location,
      });
    }
  }, [flagAlert, fullTranscript, indInfo, sessionId]);

  // ===== AGENT STATUS UPDATES =====
  // Set status to "in-session" when recording starts, "available" when it ends
  useEffect(() => {
    if (isRecording && !isPaused) {
      updateAgentLiveStatus("in-session", indInfo.individualName);
    } else if (isPaused) {
      updateAgentLiveStatus("documenting", indInfo.individualName);
    }
    return () => {
      updateAgentLiveStatus("available", null);
    };
  }, [isRecording, isPaused, indInfo.individualName]);

  // ===== GPS LOCATION CAPTURE =====
  // Capture coordinates when session starts for billing proof
  useEffect(() => {
    if (isRecording && !gpsLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: Math.round(pos.coords.accuracy),
            timestamp: new Date().toISOString(),
          };
          setGpsLocation(loc);
          console.log("[GPS] Location captured:", loc);
        },
        (err) => {
          console.warn("[GPS] Failed:", err.message);
          setGpsError(err.message);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    }
  }, [isRecording, gpsLocation]);

  // ===== CRASH RECOVERY: Check IndexedDB and localStorage =====
  useEffect(() => {
    async function checkRecovery() {
      // Check IndexedDB first
      const sessions = await getRecoveryData();
      const RECOVERY_STATUSES = ["recording", "paused", "processing", "review"];
      const interrupted = sessions.find(
        (s) => RECOVERY_STATUSES.includes(s.status) &&
        Date.now() - s.lastSaved < 24 * 60 * 60 * 1000
      );
      if (interrupted) {
        setRecoveryData(interrupted);
        setShowRecovery(true);
        return;
      }

      // Fallback: check localStorage for any crashed sessions
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("fn_session_")) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data && data.transcript && data.transcript.length > 10 &&
                Date.now() - data.lastSaved < 24 * 60 * 60 * 1000) {
              setRecoveryData(data);
              setShowRecovery(true);
              return;
            }
          } catch (e) {}
        }
      }
    }
    checkRecovery();
  }, []);

  // ===== AGGRESSIVE AUTO-SAVE: Multiple locations =====
  // Save to IndexedDB every 10s, and localStorage on every transcript change
  useEffect(() => {
    if (isRecording && !isPaused && sessionId) {
      // Immediate localStorage save on every transcript update
      try {
        localStorage.setItem(`fn_session_${sessionId}`, JSON.stringify({
          id: sessionId,
          status: "recording",
          transcript: fullTranscript,
          flaggedNotes: flaggedNotes,
          extractedFacts: extractedFacts,
          lastSaved: Date.now(),
          individualName: indInfo.individualName,
          dob: indInfo.dob,
          locationType: indInfo.locationType,
          location: indInfo.location,
          serviceType: indInfo.serviceType,
          knownMedications: location.state?.knownMedications || [],
          knownDiagnoses: location.state?.knownDiagnoses || [],
          gpsLocation,
        }));
      } catch (e) {}
    }
  }, [fullTranscript, flaggedNotes, extractedFacts]);

  // IndexedDB checkpoint every 10s
  useEffect(() => {
    if (isRecording && !isPaused && sessionId) {
      autoSaveRef.current = setInterval(async () => {
        const now = new Date();
        await saveRecoveryData({
          id: sessionId,
          status: "recording",
          transcript: fullTranscript,
          flaggedNotes: flaggedNotes,
          extractedFacts: extractedFacts,
          startTime: Date.now(),
          lastSaved: Date.now(),
          individualName: indInfo.individualName,
          individualId: indInfo.individualId,
          dob: indInfo.dob,
          locationType: indInfo.locationType,
          location: indInfo.location,
          serviceType: indInfo.serviceType,
          knownMedications: location.state?.knownMedications || [],
          knownDiagnoses: location.state?.knownDiagnoses || [],
        });
        setLastAutoSave(now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      }, AUTO_SAVE_INTERVAL);

      return () => clearInterval(autoSaveRef.current);
    }
  }, [isRecording, isPaused, sessionId, fullTranscript, flaggedNotes, extractedFacts]);

  // ===== REAL-TIME FACT EXTRACTION: Every 2 minutes =====
  useEffect(() => {
    if (isRecording && !isPaused && sessionId) {
      extractionRef.current = setInterval(async () => {
        const words = fullTranscript.trim().split(/\s+/);
        // Only extract if we have new content since last extraction
        if (words.length <= lastExtractedIndex.current + 20) return;

        // Get only the new chunk since last extraction
        const newChunk = words.slice(lastExtractedIndex.current).join(" ");
        lastExtractedIndex.current = words.length;

        setExtractionRunning(true);
        const result = await extractKeyFacts(newChunk, extractedFacts);
        setExtractionRunning(false);

        if (result.success && result.facts.length > extractedFacts.length) {
          setExtractedFacts(result.facts);
        }
      }, EXTRACTION_INTERVAL);

      return () => clearInterval(extractionRef.current);
    }
  }, [isRecording, isPaused, sessionId, fullTranscript, extractedFacts]);

  // ===== BROWSER CLOSE WARNING =====
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = "You have an active recording session. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (isRecording) {
        e.preventDefault();
        setShowEndMenu(true);
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    // Push a state so back button triggers popstate instead of navigating
    if (isRecording) {
      window.history.pushState(null, "", window.location.href);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isRecording]);

  // Waveform animation
  useEffect(() => {
    if (isRecording && !isPaused) {
      const interval = setInterval(() => {
        setWaveHeights(Array.from({ length: 30 }, () => Math.random() * 40 + 10));
      }, 150);
      return () => clearInterval(interval);
    }
  }, [isRecording, isPaused]);

  // Initialize session
  useEffect(() => {
    async function initSession() {
      if (!sessionId) {
        try {
          const session = await createSession({
            individualId: "demo",
            locationType: indInfo.locationType,
            location: "Ind Residence",
            serviceType: "ICM Weekly Visit",
          });
          setSessionId(session.id);
        } catch (e) {
          setSessionId(`local-${Date.now()}`);
        }
      }
    }
    initSession();
  }, []);

  // Initialize recorder
  useEffect(() => {
    if (sessionId && !showRecovery) {
      initialize().then((result) => {
        if (result.success) {
          startRecording();
          setRecording(true);
        }
      });
    }
    return () => {
      setRecording(false);
      cleanup();
    };
  }, [sessionId, showRecovery]);

  // ===== SESSION END HANDLERS =====

  const handleEndAndGenerate = useCallback(async () => {
    setShowEndMenu(false);
    setRecording(false);
    const sessionData = await stopRecording();
    const duration = sessionData?.duration || 0;

    // Combine existing transcript with new recording for continued sessions
    const combinedTranscript = existingTranscript
      ? existingTranscript + "\n\n[SESSION CONTINUED]\n\n" + fullTranscript
      : fullTranscript;

    if (location.state?.appointmentId) {
      completeAppointment(location.state.appointmentId);
    }

    if (sessionId && !sessionId.startsWith("local-")) {
      try {
        await updateSession(sessionId, { status: "processing", duration });
      } catch (e) {}
    }

    // KEEP recovery data alive through processing + review.
    // It is only cleared after ReviewNotePage.handleSubmit succeeds.
    await saveRecoveryData({
      id: sessionId,
      status: "processing",
      transcript: combinedTranscript,
      flaggedNotes: flaggedNotes,
      extractedFacts: extractedFacts,
      duration,
      lastSaved: Date.now(),
      individualName: indInfo.individualName,
      individualId: indInfo.individualId,
      dob: indInfo.dob,
      locationType: indInfo.locationType,
      location: indInfo.location,
      serviceType: indInfo.serviceType,
      knownMedications: location.state?.knownMedications || [],
      knownDiagnoses: location.state?.knownDiagnoses || [],
      gpsLocation,
      draftNoteId: continuedDraftId || null,
      appointmentId: location.state?.appointmentId || null,
    });
    if (extractionRef.current) clearInterval(extractionRef.current);

    navigate("/session/processing", {
      state: {
        sessionId,
        sessionData,
        draftNoteId: continuedDraftId || null,
        transcript: combinedTranscript,
        individualName: indInfo.individualName,
        individualId: indInfo.individualId,
        dob: indInfo.dob,
        locationType: indInfo.locationType,
        location: indInfo.location,
        serviceType: indInfo.serviceType,
        knownMedications: location.state?.knownMedications || [],
        knownDiagnoses: location.state?.knownDiagnoses || [],
        duration,
        appointmentId: location.state?.appointmentId,
        extractedFacts,
        gpsLocation,
      },
    });
  }, [sessionId, stopRecording, navigate, fullTranscript, existingTranscript, continuedDraftId, completeAppointment, location.state, extractedFacts, gpsLocation]);

  const handleSaveDraft = useCallback(async () => {
    setShowEndMenu(false);
    setRecording(false);
    const sessionData = await stopRecording();
    const duration = sessionData?.duration || 0;
    const durationMin = Math.round(duration / 60);
    const billingUnits = Math.max(1, Math.ceil(durationMin / 15));

    const combinedTranscript = existingTranscript
      ? existingTranscript + "\n\n[SESSION CONTINUED]\n\n" + fullTranscript
      : fullTranscript;

    if (location.state?.appointmentId) {
      completeAppointment(location.state.appointmentId);
    }
    if (extractionRef.current) clearInterval(extractionRef.current);

    const draftData = {
      individualName: indInfo.individualName,
      individualId: indInfo.individualId,
      date: new Date().toLocaleDateString(),
      serviceType: indInfo.serviceType,
      location: indInfo.location,
      locationType: indInfo.locationType,
      status: "draft",
      billingCode: "H0039",
      billingUnits: `${billingUnits} units`,
      duration: `${durationMin} min`,
      durationSeconds: duration,
      confidence: null,
      hasPriorityFlags: flaggedNotes.some((f) => f.priority === "priority"),
      noteData: null,
      transcript: combinedTranscript,
      flaggedNotes: flaggedNotes,
      extractedFacts: extractedFacts,
      dob: indInfo.dob,
      knownMedications: location.state?.knownMedications || [],
      knownDiagnoses: location.state?.knownDiagnoses || [],
    };

    if (continuedDraftId) {
      updateNote(continuedDraftId, draftData);
    } else {
      addNote(draftData);
    }

    recordDraftSession({
      individualName: indInfo.individualName,
      duration,
    });

    await saveRecoveryData({
      id: sessionId,
      status: "draft",
      transcript: combinedTranscript,
      flaggedNotes: flaggedNotes,
      extractedFacts: extractedFacts,
      duration,
      lastSaved: Date.now(),
      individualName: indInfo.individualName,
      dob: indInfo.dob,
      locationType: indInfo.locationType,
      location: indInfo.location,
      serviceType: indInfo.serviceType,
      knownMedications: location.state?.knownMedications || [],
      knownDiagnoses: location.state?.knownDiagnoses || [],
    });

    try { localStorage.removeItem(`fn_session_${sessionId}`); } catch (e) {}

    navigate("/dashboard");
  }, [sessionId, stopRecording, navigate, fullTranscript, existingTranscript, continuedDraftId, flaggedNotes, extractedFacts, addNote, recordDraftSession, completeAppointment, location.state]);

  const handlePauseAndSave = useCallback(async () => {
    setShowEndMenu(false);
    pauseRecording();

    await saveRecoveryData({
      id: sessionId,
      status: "paused",
      transcript: fullTranscript,
      flaggedNotes: flaggedNotes,
      extractedFacts: extractedFacts,
      lastSaved: Date.now(),
      individualName: indInfo.individualName,
      individualId: indInfo.individualId,
      dob: indInfo.dob,
      locationType: indInfo.locationType,
      location: indInfo.location,
      serviceType: indInfo.serviceType,
      knownMedications: location.state?.knownMedications || [],
      knownDiagnoses: location.state?.knownDiagnoses || [],
    });

    setLastAutoSave(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
  }, [sessionId, pauseRecording, fullTranscript, flaggedNotes, extractedFacts, location.state]);

  const handleRecoverSession = useCallback(async () => {
    setShowRecovery(false);

    if (recoveryData.status === "draft") {
      // Draft recovery — offer to generate note from stored transcript
      navigate("/session/processing", {
        state: {
          sessionId: recoveryData.id,
          transcript: recoveryData.transcript,
          individualName: recoveryData.individualName,
          dob: recoveryData.dob,
          locationType: recoveryData.locationType,
          location: recoveryData.location,
          serviceType: recoveryData.serviceType || "ICM Weekly Visit",
          knownMedications: recoveryData.knownMedications || [],
          knownDiagnoses: recoveryData.knownDiagnoses || [],
          duration: recoveryData.duration || 1800,
          sessionData: {
            duration: recoveryData.duration || 1800,
            flaggedSegments: recoveryData.flaggedNotes || [],
          },
        },
      });
    } else {
      // Crashed recording — go to processing
      navigate("/session/processing", {
        state: {
          sessionId: recoveryData.id,
          transcript: recoveryData.transcript,
          individualName: recoveryData.individualName,
          dob: recoveryData.dob,
          locationType: recoveryData.locationType,
          location: recoveryData.location,
          serviceType: recoveryData.serviceType || "ICM Weekly Visit",
          knownMedications: recoveryData.knownMedications || [],
          knownDiagnoses: recoveryData.knownDiagnoses || [],
          duration: recoveryData.duration || 1800,
          sessionData: {
            duration: recoveryData.duration || 1800,
            flaggedSegments: recoveryData.flaggedNotes || [],
          },
        },
      });
    }
  }, [recoveryData, navigate]);

  const handleDiscardRecovery = useCallback(async () => {
    if (recoveryData) {
      await clearRecoveryData(recoveryData.id);
    }
    setShowRecovery(false);
    setRecoveryData(null);
  }, [recoveryData]);

  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  }, [isPaused, pauseRecording, resumeRecording]);

  return (
    <div className="min-h-screen bg-surface-primary flex flex-col">
      {/* Header */}
      <header
        className="px-5 py-3 flex items-center justify-between"
        style={{
          background: "rgba(10,15,26,0.95)",
          borderBottom: "1px solid rgba(99,179,237,0.08)",
        }}
      >
        <div>
          <div className="text-sm text-gray-500">{indInfo.individualName}</div>
          <div className="text-[11px] text-gray-600">{indInfo.serviceType} · {indInfo.locationType}</div>
        </div>
        <div className="flex items-center gap-3">
          {lastAutoSave && (
            <div className="flex items-center gap-1">
              <Save size={10} className="text-green-500" />
              <span className="text-[10px] text-green-500 font-medium">Saved {lastAutoSave}</span>
            </div>
          )}
          {gpsLocation ? (
            <div className="flex items-center gap-1" title={`${gpsLocation.latitude.toFixed(4)}, ${gpsLocation.longitude.toFixed(4)} (±${gpsLocation.accuracy}m)`}>
              <MapPin size={10} className="text-teal-400" />
              <span className="text-[10px] text-teal-400 font-medium">GPS ✓</span>
            </div>
          ) : gpsError ? (
            <div className="flex items-center gap-1" title={gpsError}>
              <MapPin size={10} className="text-yellow-400" />
              <span className="text-[10px] text-yellow-400 font-medium">No GPS</span>
            </div>
          ) : isRecording ? (
            <div className="flex items-center gap-1">
              <MapPin size={10} className="text-gray-500 animate-pulse" />
              <span className="text-[10px] text-gray-500 font-medium">GPS...</span>
            </div>
          ) : null}
          {extractedFacts.length > 0 && (
            <button
              className="flex items-center gap-1"
              onClick={() => setShowFacts(!showFacts)}
            >
              <Shield size={10} className={extractionRunning ? "text-yellow-400 animate-pulse" : "text-teal-400"} />
              <span className="text-[10px] text-teal-400 font-medium">{extractedFacts.length} facts</span>
            </button>
          )}
          <div className="flex items-center gap-1.5">
            {isOnline ? (
              <>
                <Wifi size={12} className="text-green-400" />
                <span className="text-[11px] text-green-400 font-semibold">Online</span>
              </>
            ) : (
              <>
                <WifiOff size={12} className="text-yellow-400" />
                <span className="text-[11px] text-yellow-400 font-semibold">Offline</span>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col px-5 py-4">
        {/* Continuation banner */}
        {isContinuation && (
          <div
            className="flex items-center gap-2.5 p-3 rounded-xl mb-3"
            style={{ background: "rgba(79,209,197,0.06)", border: "1px solid rgba(79,209,197,0.15)" }}
          >
            <Mic size={14} className="text-teal-400 shrink-0" />
            <div>
              <div className="text-xs font-semibold text-teal-400">Continued Session</div>
              <div className="text-[10px] text-gray-500">Previous transcript preserved · New recording will be combined</div>
            </div>
          </div>
        )}

        {/* Timer */}
        <div className="text-center mt-4">
          <div
            className="text-5xl font-extrabold text-gray-100 tracking-widest"
            style={{ fontFamily: "var(--font-mono), monospace", fontVariantNumeric: "tabular-nums" }}
          >
            {formattedTime}
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <div
              className={`w-2 h-2 rounded-full ${isPaused ? "" : "animate-blink"}`}
              style={{ background: isPaused ? "#ecc94b" : "#dc2626" }}
            />
            <span
              className="text-sm font-semibold tracking-wider uppercase"
              style={{ color: isPaused ? "#ecc94b" : "#fc8181" }}
            >
              {isPaused ? "Paused — Session Saved" : "Recording"}
            </span>
          </div>
        </div>

        {/* Section Indicator */}
        {currentSection && (
          <div className="text-center mt-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/10 text-teal-400 border border-teal-500/20">
              Section: {currentSection}
            </span>
          </div>
        )}

        {/* Flagging Indicator */}
        {isFlagging && (
          <div className="text-center mt-2">
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold border animate-pulse"
              style={{
                background: flagPriority === "priority" ? "rgba(252,129,129,0.15)" : "rgba(236,201,75,0.15)",
                borderColor: flagPriority === "priority" ? "rgba(252,129,129,0.3)" : "rgba(236,201,75,0.25)",
                color: flagPriority === "priority" ? "#fc8181" : "#ecc94b",
              }}
            >
              <Flag size={12} />
              {flagPriority === "priority" ? "🚨 PRIORITY — Capturing..." : "Capturing flagged note..."}
            </span>
          </div>
        )}

        {/* Waveform */}
        <div className="flex justify-center items-center gap-[3px] my-8 h-14">
          {waveHeights.map((h, i) => (
            <div
              key={i}
              className="w-[3px] rounded-sm wave-bar"
              style={{
                height: isPaused ? 4 : h,
                background: `rgba(99, 179, 237, ${isPaused ? 0.2 : 0.3 + Math.random() * 0.5})`,
              }}
            />
          ))}
        </div>

        {/* Live Transcript */}
        <div
          className="rounded-xl p-4 flex-1 max-h-44 overflow-y-auto mb-4"
          style={{
            background: "rgba(15,23,42,0.5)",
            border: "1px solid rgba(99,179,237,0.06)",
          }}
        >
          <div className="text-[11px] font-bold text-accent-teal tracking-wider uppercase mb-2">
            Live Transcript
          </div>
          <div className="text-sm text-gray-400 leading-relaxed">
            {fullTranscript || transcript.interim || (
              <span className="text-gray-600 italic">Listening...</span>
            )}
          </div>
        </div>

        {/* Flagged Notes */}
        {flaggedNotes.length > 0 && (
          <div className="mb-4">
            <div className="text-[11px] font-bold text-yellow-400 tracking-wider uppercase mb-2">
              Flagged Notes ({flaggedNotes.length})
            </div>
            {flaggedNotes.map((note, i) => (
              <div
                key={i}
                className="flex gap-2.5 py-2.5 px-3 rounded-lg mb-1.5"
                style={{
                  background: note.priority === "priority"
                    ? "rgba(252,129,129,0.08)"
                    : "rgba(236,201,75,0.04)",
                  border: note.priority === "priority"
                    ? "1px solid rgba(252,129,129,0.2)"
                    : "1px solid rgba(236,201,75,0.08)",
                }}
              >
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <span
                    className="text-[11px] font-bold whitespace-nowrap font-mono"
                    style={{ color: note.priority === "priority" ? "#fc8181" : "#ecc94b" }}
                  >
                    {Math.floor(note.startTime / 60)}:{String(note.startTime % 60).padStart(2, "0")}
                  </span>
                  {note.priority === "priority" && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 uppercase">
                      Priority
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-300 leading-relaxed">
                  {note.transcript || "(Capturing...)"}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Extracted Facts Safety Net */}
        {showFacts && extractedFacts.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Shield size={12} className="text-teal-400" />
                <span className="text-[11px] font-bold text-teal-400 tracking-wider uppercase">
                  Captured Facts ({extractedFacts.length})
                </span>
              </div>
              <button
                className="text-[10px] text-gray-600"
                onClick={() => setShowFacts(false)}
              >
                Hide
              </button>
            </div>
            {extractedFacts.map((fact, i) => (
              <div
                key={i}
                className="flex gap-2 py-1.5 px-3 rounded-lg mb-1"
                style={{ background: "rgba(79,209,197,0.04)", border: "1px solid rgba(79,209,197,0.08)" }}
              >
                <span
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 uppercase"
                  style={{
                    background: fact.category === "SAFETY" || fact.category === "SUBSTANCE"
                      ? "rgba(252,129,129,0.15)"
                      : fact.category === "MEDICATION"
                      ? "rgba(99,179,237,0.15)"
                      : "rgba(79,209,197,0.1)",
                    color: fact.category === "SAFETY" || fact.category === "SUBSTANCE"
                      ? "#fc8181"
                      : fact.category === "MEDICATION"
                      ? "#63b3ed"
                      : "#4fd1c5",
                  }}
                >
                  {fact.category}
                </span>
                <span className="text-xs text-gray-400 leading-relaxed">{fact.text}</span>
              </div>
            ))}
            {extractionRunning && (
              <div className="text-[10px] text-yellow-400 text-center mt-1 animate-pulse">
                Extracting new facts...
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Controls */}
        <div className="flex justify-center items-center gap-8 mt-auto pb-6">
          <button className="flex flex-col items-center gap-1" onClick={() => manualFlag(isFlagging ? "normal" : "normal")}>
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{
                background: isFlagging ? "rgba(236,201,75,0.25)" : "rgba(236,201,75,0.1)",
                border: isFlagging ? "2px solid rgba(236,201,75,0.6)" : "2px solid rgba(236,201,75,0.3)",
              }}
            >
              <Flag size={18} className="text-yellow-400" />
            </div>
            <span className="text-[10px] text-gray-500 font-semibold">{isFlagging ? "End Flag" : "Flag"}</span>
          </button>

          <button
            className="flex flex-col items-center gap-1"
            onClick={() => setShowEndMenu(true)}
          >
            <div
              className="w-[72px] h-[72px] rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #dc2626, #b91c1c)",
                boxShadow: "0 0 30px rgba(220,38,38,0.3)",
              }}
            >
              <Square size={28} fill="white" className="text-white" />
            </div>
            <span className="text-[10px] text-red-400 font-semibold">Stop</span>
          </button>

          <button className="flex flex-col items-center gap-1" onClick={handlePauseResume}>
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: "rgba(99,179,237,0.1)", border: "2px solid rgba(99,179,237,0.3)" }}
            >
              {isPaused ? <Play size={18} className="text-blue-400" /> : <Pause size={18} className="text-blue-400" />}
            </div>
            <span className="text-[10px] text-gray-500 font-semibold">{isPaused ? "Resume" : "Pause"}</span>
          </button>
        </div>
      </div>

      {/* ===== END SESSION MENU ===== */}
      {showEndMenu && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/70 px-4 pb-8">
          <div
            className="w-full max-w-sm rounded-2xl overflow-hidden animate-slide-up"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(99,179,237,0.15)",
            }}
          >
            <div className="p-5 pb-3">
              <h3 className="text-lg font-bold text-gray-100 mb-1">Session Options</h3>
              <p className="text-sm text-gray-500">
                Duration: <span className="text-gray-300 font-semibold">{formattedTime}</span>
                {lastAutoSave && <span className="text-green-500 ml-2">· Last saved {lastAutoSave}</span>}
              </p>
            </div>

            <div className="px-3 pb-3 flex flex-col gap-2">
              {/* Continue Recording */}
              <button
                className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors"
                style={{ background: "rgba(99,179,237,0.06)", border: "1px solid rgba(99,179,237,0.1)" }}
                onClick={() => setShowEndMenu(false)}
              >
                <Play size={20} className="text-blue-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-gray-200">Continue Recording</div>
                  <div className="text-xs text-gray-500 mt-0.5">Go back to the session</div>
                </div>
              </button>

              {/* Pause & Save */}
              <button
                className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors"
                style={{ background: "rgba(236,201,75,0.06)", border: "1px solid rgba(236,201,75,0.1)" }}
                onClick={handlePauseAndSave}
              >
                <Clock size={20} className="text-yellow-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-gray-200">Pause & Save</div>
                  <div className="text-xs text-gray-500 mt-0.5">Take a break — resume later. Everything is saved.</div>
                </div>
              </button>

              {/* Save Draft — End without generating */}
              <button
                className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors"
                style={{ background: "rgba(79,209,197,0.06)", border: "1px solid rgba(79,209,197,0.1)" }}
                onClick={handleSaveDraft}
              >
                <FileText size={20} className="text-teal-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-gray-200">End & Save Draft</div>
                  <div className="text-xs text-gray-500 mt-0.5">Stop recording. Generate the note later when you're ready.</div>
                </div>
              </button>

              {/* End & Generate Now */}
              <button
                className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors"
                style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.15)" }}
                onClick={handleEndAndGenerate}
              >
                <Square size={20} className="text-red-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-gray-200">End & Generate Note</div>
                  <div className="text-xs text-gray-500 mt-0.5">Stop recording and generate the clinical note now.</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CRASH RECOVERY MODAL ===== */}
      {showRecovery && recoveryData && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(236,201,75,0.2)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Save size={18} className="text-yellow-400" />
              <h3 className="text-lg font-bold text-gray-100">Session Recovered</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-2">
              An interrupted session was found for <span className="text-gray-200 font-semibold">{recoveryData.individualName}</span>.
            </p>
            <p className="text-xs text-gray-500 mb-5">
              Last saved: {new Date(recoveryData.lastSaved).toLocaleString()}
              {recoveryData.transcript && (
                <span> · {recoveryData.transcript.split(" ").length} words captured</span>
              )}
              {recoveryData.extractedFacts?.length > 0 && (
                <span> · {recoveryData.extractedFacts.length} facts extracted</span>
              )}
            </p>
            <div className="flex flex-col gap-2">
              {/* Option 1: Continue recording */}
              <button
                className="w-full py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #38a169, #2f855a)" }}
                onClick={async () => {
                  // Continue session — reload ActiveSessionPage with existing transcript
                  const recData = { ...recoveryData };
                  await clearRecoveryData(recData.id);
                  setShowRecovery(false);
                  setRecoveryData(null);
                  navigate(`/session/active`, {
                    replace: true,
                    state: {
                      individualName: recData.individualName,
                      individualId: recData.individualId,
                      dob: recData.dob,
                      locationType: recData.locationType,
                      location: recData.location,
                      serviceType: recData.serviceType,
                      knownMedications: recData.knownMedications || [],
                      knownDiagnoses: recData.knownDiagnoses || [],
                      existingTranscript: recData.transcript || "",
                      existingFlaggedNotes: recData.flaggedNotes || [],
                      existingExtractedFacts: recData.extractedFacts || [],
                    },
                  });
                }}
              >
                ▶ Continue Session
              </button>
              {/* Option 2: Generate note from what we have */}
              <button
                className="w-full py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
                onClick={handleRecoverSession}
              >
                Generate Note from Recording
              </button>
              {/* Option 3: Save as draft */}
              <button
                className="w-full py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(236,201,75,0.06)", color: "#ecc94b", border: "1px solid rgba(236,201,75,0.15)" }}
                onClick={() => {
                  const dur = recoveryData.duration || 1800;
                  const durMin = Math.round(dur / 60);
                  addNote({
                    individualName: recoveryData.individualName || "Individual",
                    date: new Date(recoveryData.lastSaved).toLocaleDateString(),
                    serviceType: recoveryData.serviceType || "ICM Weekly Visit",
                    location: recoveryData.location || "Community",
                    locationType: recoveryData.locationType || "Out-of-Clinic",
                    status: "draft",
                    billingCode: "H0039",
                    billingUnits: `${Math.max(1, Math.ceil(durMin / 15))} units`,
                    duration: `${durMin} min`,
                    durationSeconds: dur,
                    confidence: null,
                    hasPriorityFlags: false,
                    noteData: null,
                    transcript: recoveryData.transcript || "",
                    flaggedNotes: recoveryData.flaggedNotes || [],
                    extractedFacts: recoveryData.extractedFacts || [],
                    dob: recoveryData.dob || "",
                    knownMedications: recoveryData.knownMedications || [],
                    knownDiagnoses: recoveryData.knownDiagnoses || [],
                  });
                  recordDraftSession({
                    individualName: recoveryData.individualName || "Individual",
                    duration: dur,
                  });
                  handleDiscardRecovery();
                  navigate("/dashboard");
                }}
              >
                Save as Draft — Generate Later
              </button>
              {/* Option 4: Discard */}
              <button
                className="w-full py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={handleDiscardRecovery}
              >
                Discard & Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== BIG VISIBLE FLAG ALERT ===== */}
      {flagAlert && (
        <div className="fixed top-0 left-0 right-0 z-[300] animate-slide-up" style={{ maxWidth: 480, margin: "0 auto" }}>
          {flagAlert.tier === 3 ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
                border: "2px solid rgba(252,129,129,0.4)",
                boxShadow: "0 8px 32px rgba(220,38,38,0.4)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">🚨</span>
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm tracking-wide uppercase">
                    {flagAlert.category || "Safety Alert"} — Supervisor Notified
                  </div>
                  <div className="text-red-200 text-xs mt-0.5">"{flagAlert.phrase}"</div>
                </div>
              </div>
            </div>
          ) : flagAlert.tier === 2 ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(237,137,54,0.95), rgba(221,107,32,0.95))",
                border: "2px solid rgba(246,173,85,0.5)",
                boxShadow: "0 8px 32px rgba(237,137,54,0.3)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">📋</span>
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm tracking-wide">Clinical Indicator Captured</div>
                  <div className="text-orange-200 text-xs mt-0.5">"{flagAlert.phrase}" — flagged in note</div>
                </div>
              </div>
            </div>
          ) : flagAlert.tier === 1 ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(236,201,75,0.95), rgba(214,158,46,0.95))",
                border: "2px solid rgba(236,201,75,0.5)",
                boxShadow: "0 8px 32px rgba(236,201,75,0.3)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">📌</span>
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm tracking-wide" style={{ color: "#1a1a1a" }}>Local Flag Noted</div>
                  <div className="text-xs mt-0.5" style={{ color: "#4a3a00" }}>"{flagAlert.phrase}" — saved to session</div>
                </div>
              </div>
            </div>
          ) : flagAlert.priority === "priority" ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
                border: "2px solid rgba(252,129,129,0.4)",
                boxShadow: "0 8px 32px rgba(220,38,38,0.4)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">🚨</span>
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm tracking-wide uppercase">Priority Flag Captured</div>
                  <div className="text-red-200 text-xs mt-0.5">"{flagAlert.phrase}"</div>
                </div>
              </div>
            </div>
          ) : flagAlert.type === "startNote" ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(236,201,75,0.95), rgba(214,158,46,0.95))",
                border: "2px solid rgba(236,201,75,0.5)",
                boxShadow: "0 8px 32px rgba(236,201,75,0.3)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">📌</span>
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm tracking-wide" style={{ color: "#1a1a1a" }}>Flagging Note...</div>
                  <div className="text-xs mt-0.5" style={{ color: "#4a3a00" }}>Say "end note" or tap Flag to close</div>
                </div>
              </div>
            </div>
          ) : flagAlert.type === "endNote" ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(72,187,120,0.95), rgba(56,161,105,0.95))",
                border: "2px solid rgba(72,187,120,0.5)",
                boxShadow: "0 8px 32px rgba(72,187,120,0.3)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">✅</span>
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm tracking-wide">Note Captured</div>
                  <div className="text-green-200 text-xs mt-0.5">Flagged note saved to session</div>
                </div>
              </div>
            </div>
          ) : flagAlert.type === "section" ? (
            <div
              className="mx-3 mt-3 p-4 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, rgba(79,209,197,0.95), rgba(56,178,172,0.95))",
                border: "2px solid rgba(79,209,197,0.5)",
                boxShadow: "0 8px 32px rgba(79,209,197,0.3)",
                backdropFilter: "blur(20px)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-xl">📋</span>
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-sm tracking-wide">Section: {flagAlert.phrase}</div>
                  <div className="text-teal-200 text-xs mt-0.5">Routing content to this section</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}